<template>
    <div class="px-3">
        <h3
            class="my-3 flex border-b border-gray-500 pb-2 text-lg font-bold leading-6 text-white"
        >
            Avaliar Saque 
            {{ cashout.created_at }}
            <router-link
                v-if="cashout"
                :to="{
                    name: 'finnancial.cashout-details',
                    params: { cashout_id: cashout.id },
                }"
                target="_blank"
                class="ml-2 flex text-indigo-400 hover:cursor-pointer hover:text-indigo-600"
            >
                #{{ cashout.id }}
                <ArrowTopRightOnSquareIcon class="h-5 w-5" aria-hidden="true" />
            </router-link>
            <router-link
                v-if="cashout"
                :to="{
                    name: 'user.audit.commissions',
                    params: { user_id: cashout.user_id },
                }"
                target="_blank"
                class="ml-2 flex text-indigo-400 hover:cursor-pointer hover:text-indigo-600"
            >
                #USER
                <ArrowTopRightOnSquareIcon class="h-5 w-5" aria-hidden="true" />
            </router-link>
            <span class="ml-1">
                <cashout-status :status="cashout.status" />
            </span>
        </h3>

        <div class="my-3">
            <cashout-tags :cashout="cashout" />
        </div>

        <router-link
            v-if="userAudits && userAudits.length > 0"
            v-for="userAudit in userAudits"
            :key="userAudit.id"
            :to="{
                name: 'audit.interactions',
                params: { audit_id: userAudit.id },
            }"
            target="_blank"
            class="mb-3 flex justify-end text-red-300 hover:cursor-pointer hover:underline"
        >
            <ExclamationCircleIcon
                class="h-6 w-6"
                v-tooltip="'Usuário em Auditoria'"
            />
            Auditoria
            {{ userAudit.status === "closed" ? "finalizada" : "iniciada" }}
            {{ userAudit.created_at }}
        </router-link>

        <div class="mb-3 grid grid-cols-3 gap-1" v-if="cashout">
            <info-col title="VALOR SOLICITADO">
                {{ $root.formatCurrency(cashout.amount) }}
            </info-col>
            <info-col title="TAXA APLICADA">
                {{ $root.formatCurrency(cashout.fee) }}
            </info-col>
            <info-col title="VALOR A RECEBER">
                {{ $root.formatCurrency(cashout.net_value) }}
            </info-col>
        </div>

        <!-- Cards organizados e colapsáveis -->
        <div class="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
            <collapsible-info-card 
                title="Análise do Sistema"
                icon="ExclamationCircleIcon"
                :collapsed="!audit?.analysis"
                :priority="systemAnalysisPriority"
                :summary="systemAnalysisSummary"
                :badge="systemAnalysisBadge"
                :loading="!audit?.analysis"
            >
                <system-analysis :analysis="audit?.analysis" @data-loaded="updateSystemAnalysisSummary" />
            </collapsible-info-card>
            
            <collapsible-info-card 
                title="BINs de Cartão Mais Usados"
                icon="CreditCardIcon"
                :collapsed="true"
                :summary="binSummary"
                :priority="binPriority"
                :badge="binBadge"
                :loading="binSummary === ''"
            >
                <bin-usage-card :cashout="cashout" @data-loaded="updateBinSummary" />
            </collapsible-info-card>

            <collapsible-info-card 
                title="Métodos de Pagamento"
                icon="BanknotesIcon"
                :collapsed="true"
                :summary="paymentMethodSummary"
                :priority="paymentMethodPriority"
                :badge="paymentMethodBadge"
                :loading="paymentMethodSummary === ''"
            >
                <payment-method-card :cashout="cashout" @data-loaded="updatePaymentMethodSummary" />
            </collapsible-info-card>

            <collapsible-info-card 
                title="Tipos de Comissão"
                icon="ChartBarIcon"
                :collapsed="true"
                :summary="commissionTypesSummary"
                :priority="commissionTypesPriority"
                :badge="commissionTypesBadge"
                :loading="commissionTypesSummary === ''"
                v-if="audit && audit.commission_types"
            >
                <div class="flex flex-col gap-2">
                    <div
                        v-for="(commission, type) in audit.commission_types"
                        :key="type"
                        class="flex items-center"
                    >
                        <span 
                            class="text-sm font-medium px-2 py-1 rounded"
                            :class="getCommissionTypeColor(commission.type)"
                        >
                            <span class="text-xs">
                                {{ formatCommissionType(commission.type) }}
                            </span>
                        </span>
                        <span class="text-sm font-semibold text-gray-900 dark:text-white ml-2">
                            {{ $root.formatCurrency(commission.amount) }}
                        </span>
                    </div>
                </div>
            </collapsible-info-card>
        </div>

        <!-- Agora o CashoutPayables renderiza seus próprios cards -->
        <cashout-payables 
            :cashout="cashout" 
            :products="true"
            :payable-details="payableDetails"
        />

        <form @submit.prevent="submit">
            <cards-radio-group
                v-model="form.status"
                :options="[
                    { label: 'Aprovado', value: 'approved' },
                    { label: 'Atenção', value: 'warning' },
                    { label: 'Reprovado', value: 'rejected' },
                ]"
            />

            <label
                for="content"
                class="mb-2 mt-2 block text-sm font-medium text-gray-900 dark:text-gray-400"
            >
                Descrição
            </label>
            <textarea
                v-model="form.content"
                id="content"
                rows="4"
                class="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 dark:focus:border-blue-500 dark:focus:ring-blue-500"
            ></textarea>
            <submit-button class="mt-4 w-full py-4" :loading="loading">
                Enviar
                <span v-if="isMacOS" class="ml-2 text-sm text-gray-400">⌘ + Enter</span>
            </submit-button>
        </form>
    </div>
</template>

<script>
import Modal from "../../layouts/Modal.vue";
import CardsRadioGroup from "../../forms/CardsRadioGroup.vue";
import { Store } from "../../../stores/store.js";
import { mapWritableState } from "pinia";
import InfoCol from "../../layouts/InfoCol.vue";
import Card from "../../layouts/Card.vue";
import SubmitButton from "../../forms/SubmitButton.vue";
import CommissionCards from "./CommissionCards.vue";
import CashoutPayables from "../../../views/finnancial/cashout/CashoutPayables.vue";
import CashoutTags from "../CashoutTags.vue";
import { ArrowTopRightOnSquareIcon } from "@heroicons/vue/24/outline";
import CashoutStatus from "../../layouts/CashoutStatus.vue";
import CommissionType from '../../../mixins/CommissionType.vue'
import { ExclamationCircleIcon, CreditCardIcon, BanknotesIcon, CurrencyDollarIcon, ChartBarIcon } from "@heroicons/vue/24/outline";
import SystemAnalysis from './SystemAnalysis.vue'
import BinUsageCard from './BinUsageCard.vue'
import PaymentMethodCard from './PaymentMethodCard.vue'
import CollapsibleInfoCard from './CollapsibleInfoCard.vue'

export default {
    components: {
        CashoutStatus,
        ArrowTopRightOnSquareIcon,
        CashoutTags,
        CashoutPayables,
        CommissionCards,
        SubmitButton,
        Card,
        InfoCol,
        CardsRadioGroup,
        Modal,
        ExclamationCircleIcon,
        CreditCardIcon,
        BanknotesIcon,
        CurrencyDollarIcon,
        ChartBarIcon,
        SystemAnalysis,
        BinUsageCard,
        PaymentMethodCard,
        CollapsibleInfoCard,
    },
    mixins: [CommissionType],
    props: {
        cashout: {
            type: Object,
            required: true
        },
        review: {
            type: Object,
            required: true
        },
        payableDetails: {
            type: Object,
            default: null
        }
    },
    emits: ["close", "fetch"],

    data() {
        return {
            loading: false,
            isMacOS: navigator.platform.toUpperCase().indexOf('MAC') >= 0,
            // Dados para os summaries dos cards
            binSummary: '',
            binPriority: 'normal',
            binBadge: null,
            paymentMethodSummary: '',
            paymentMethodPriority: 'normal',
            paymentMethodBadge: null,
            systemAnalysisSummary: '',
            systemAnalysisPriority: 'normal',
            systemAnalysisBadge: null,
            commissionTypesSummary: '',
            commissionTypesPriority: 'normal',
            commissionTypesBadge: null,
            form: {
                status: "approved",
                content: "",
                cashout_id: this.cashout.id,
                user_id: "",
                balance: {
                    available: 0,
                    pending: 0,
                },
                commissions: {},
            },
        };
    },

    watch: {
        cashout() {
            if (this.cashout) {
                this.form.cashout_id = this.cashout.id;
            }
        },
        audit: {
            immediate: true,
            handler(newAudit) {
                if (newAudit && newAudit.commission_types) {
                    // Converte o objeto em array para processamento
                    const commissionTypesArray = Object.entries(newAudit.commission_types).map(([key, commission]) => ({
                        type: commission.type,
                        amount: commission.amount,
                        percentage: (commission.amount / this.cashout.amount) * 100
                    }));
                    this.updateCommissionTypesSummary(commissionTypesArray);
                }
            }
        }
    },

    computed: {
        ...mapWritableState(Store, ["authUser"]),
        audit() {
            return this.payableDetails ? this.payableDetails.audit : null;
        },
        userAudits() {
            return this.payableDetails ? this.payableDetails.user_audits : null;
        },
    },

    mounted() {
        this.form.user_id = this.cashout.user_id;
        this.form = this.review;
        if (this.form.status === "pending") {
            this.form.status = "approved";
        }

        // Adiciona listener para o atalho de teclado
        document.addEventListener('keydown', this.handleKeyPress);
    },

    beforeUnmount() {
        // Remove o listener quando o componente é destruído
        document.removeEventListener('keydown', this.handleKeyPress);
    },

    methods: {
        handleKeyPress(event) {
            // Verifica se é MacOS e se pressionou Cmd+Enter
            if (this.isMacOS && event.metaKey && event.key === 'Enter') {
                this.submit();
            }
        },
        submit() {
            this.loading = true;
            axios
                .put("/api/cashout/" + this.cashout.id + "/review/" + this.review.id, this.form,)
                .then(({ data }) => {
                    this.loading = false;
                    this.$emit("fetch");
                })
                .catch((error) => {
                    this.loading = false;
                });
        },
        fetchBalance() {
            this.loading = true;
            axios
                .get("/api/cashout/" + this.cashout.id + "/balance")
                .then(({ data }) => {
                    this.form.balance = {
                        available: data.event_properties.available_balance,
                        pending: data.event_properties.pending_balance,
                    };
                })
                .finally(() => (this.loading = false));
        },
        updateCommissions(event) {
            this.form.commissions = event.commissions;
        },
        fetchPayableDetails() {
            axios.get(`/api/cashout/${this.cashout.id}/payables/details`)
                .then(({ data }) => {
                    this.payableDetails = data;
                })
                .catch(error => {
                    console.error("Erro ao buscar detalhes dos recebíveis:", error);
                });
        },
        updateBinSummary(data) {
            if (!data || !data.top_bins || data.top_bins.length === 0) {
                this.binSummary = 'Nenhum BIN encontrado';
                this.binPriority = 'normal';
                this.binBadge = null;
                return;
            }
            
            const topBins = data.top_bins;
            const summary = data.summary;
            
            // Determina prioridade baseada no resumo geral se disponível
            if (summary && summary.high_fraud_bins > 0) {
                this.binPriority = 'danger';
                this.binBadge = `${summary.high_fraud_bins}/${summary.total_unique_bins} alto risco`;
                this.binSummary = `${summary.total_unique_bins} BINs, ${summary.high_fraud_bins} alto risco`;
            } else if (summary && summary.medium_fraud_bins > 0) {
                this.binPriority = 'warning';
                this.binBadge = `${summary.medium_fraud_bins}/${summary.total_unique_bins} médio risco`;
                this.binSummary = `${summary.total_unique_bins} BINs, ${summary.medium_fraud_bins} médio risco`;
            } else {
                // Fallback para análise dos top bins se não houver resumo
                const highFraudBins = topBins.filter(bin => bin.fraud_level === 'high').length;
                const mediumFraudBins = topBins.filter(bin => bin.fraud_level === 'medium').length;
                const totalBins = summary?.total_unique_bins || topBins.length;
                
                if (highFraudBins > 0) {
                    this.binPriority = 'danger';
                    this.binBadge = `${highFraudBins}/${totalBins} alto risco`;
                    this.binSummary = `${totalBins} BINs, ${highFraudBins} alto risco`;
                } else if (mediumFraudBins > 0) {
                    this.binPriority = 'warning';
                    this.binBadge = `${mediumFraudBins}/${totalBins} médio risco`;
                    this.binSummary = `${totalBins} BINs, ${mediumFraudBins} médio risco`;
                } else {
                    this.binPriority = 'normal';
                    this.binBadge = `${totalBins} BINs`;
                    this.binSummary = `${totalBins} BINs únicos`;
                }
            }
        },
        updatePaymentMethodSummary(data) {
            if (!data || data.length === 0) {
                this.paymentMethodSummary = 'Nenhum método encontrado';
                this.paymentMethodPriority = 'normal';
                this.paymentMethodBadge = null;
                return;
            }
            
            const highConcentration = data.filter(method => method.percentage > 70).length;
            const mediumConcentration = data.filter(method => method.percentage > 50).length;
            
            if (highConcentration > 0) {
                this.paymentMethodPriority = 'warning';
                this.paymentMethodBadge = 'Alta concentração';
                this.paymentMethodSummary = `${data.length} métodos, concentração alta`;
            } else if (mediumConcentration > 0) {
                this.paymentMethodPriority = 'warning';
                this.paymentMethodBadge = 'Concentração média';
                this.paymentMethodSummary = `${data.length} métodos, concentração média`;
            } else {
                this.paymentMethodPriority = 'normal';
                this.paymentMethodBadge = `${data.length} métodos`;
                this.paymentMethodSummary = `${data.length} métodos distribuídos`;
            }
        },
        updateSystemAnalysisSummary(analysis) {
            if (!analysis) {
                this.systemAnalysisSummary = 'Análise não disponível';
                this.systemAnalysisPriority = 'normal';
                this.systemAnalysisBadge = null;
                return;
            }
            
            // Determina a prioridade baseada na confiança e análise
            if (analysis.analysis === 'Reprovar' || analysis.confidence < 30) {
                this.systemAnalysisPriority = 'danger';
            } else if (analysis.analysis === 'Atenção' || (analysis.confidence >= 30 && analysis.confidence < 70)) {
                this.systemAnalysisPriority = 'warning';
            } else {
                this.systemAnalysisPriority = 'normal';
            }
            
            this.systemAnalysisBadge = `${analysis.confidence}%`;
            this.systemAnalysisSummary = `${analysis.analysis} (${analysis.confidence}% confiança)`;
        },
        updateCommissionTypesSummary(data) {
            if (!data || data.length === 0) {
                this.commissionTypesSummary = 'Nenhum tipo de comissão encontrado';
                this.commissionTypesPriority = 'normal';
                this.commissionTypesBadge = null;
                return;
            }
            
            const highConcentration = data.filter(commission => commission.percentage > 70).length;
            const mediumConcentration = data.filter(commission => commission.percentage > 50).length;
            
            if (highConcentration > 0) {
                this.commissionTypesPriority = 'warning';
                this.commissionTypesBadge = 'Alta concentração';
                this.commissionTypesSummary = `${data.length} tipos de comissão, concentração alta`;
            } else if (mediumConcentration > 0) {
                this.commissionTypesPriority = 'warning';
                this.commissionTypesBadge = 'Concentração média';
                this.commissionTypesSummary = `${data.length} tipos de comissão, concentração média`;
            } else {
                this.commissionTypesPriority = 'normal';
                this.commissionTypesBadge = `${data.length} tipos de comissão`;
                this.commissionTypesSummary = `${data.length} tipos de comissão distribuídos`;
            }
        },
    },
};
</script>
